export class AudioEngine {
  readonly ctx: AudioContext;
  readonly master: GainNode;
  private muted = false;
  private masterLevel = 0.7;

  constructor() {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.master = this.ctx.createGain();
    this.master.gain.value = this.masterLevel;
    this.master.connect(this.ctx.destination);
  }

  /** Resume the context — required on first user gesture before audio can play. */
  async resume(): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  /** Suspend the context — stops all sound and frees CPU while backgrounded. */
  async suspend(): Promise<void> {
    if (this.ctx.state === 'running') await this.ctx.suspend();
  }

  setMuted(m: boolean): void {
    this.muted = m;
    this.master.gain.setTargetAtTime(m ? 0 : this.masterLevel, this.ctx.currentTime, 0.05);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Set the unmuted master volume level (0..1). Persists across mute toggles —
   *  toggling mute off restores this value. Default 0.7. */
  setMasterVolume(v: number): void {
    this.masterLevel = clamp01(v);
    if (!this.muted) {
      this.master.gain.setTargetAtTime(this.masterLevel, this.ctx.currentTime, 0.05);
    }
  }

  getMasterVolume(): number {
    return this.masterLevel;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
